/**
 * Central configuration for Eggs Fighting Game.
 * All tuneable values live here — zero magic numbers in component code.
 */
export const GAME_CONFIG = {
  /** Total number of rounds played per match; player with most wins at the end wins */
  maxRounds: 1,

  timing: {
    /** Duration of each countdown step (3 → 2 → 1) in milliseconds */
    countdownStepMs: 1000,
    /** How long "FIGHT!" stays visible before eggs start moving, in ms */
    fightDisplayMs: 650,
    /** How long the fight animation sequence runs before collision resolves, in ms.
     *  Must cover all 3 clashes (see animation.fightClashes). */
    fightDurationMs: 2800,
    /** How long the impact state is held (star + cracks visible) in ms */
    impactHoldMs: 1050,
    /** How long the round result overlay is displayed, in ms */
    roundResultMs: 2600,
    /** Delay before starting the next round countdown, in ms */
    nextRoundDelayMs: 450,
  },

  animation: {
    /**
     * Pixels each egg is pushed toward its own side at idle/countdown.
     * Larger = eggs start further apart. Tuned for the current panel width.
     */
    eggIdleOffsetX: 55,

    /**
     * Degrees each egg tilts toward its own side at idle/countdown.
     * Left egg tilts left (negative), right egg tilts right (positive).
     */
    eggIdleRotate: 14,

    /**
     * Pixels each egg translates toward the center during the fight phase.
     * At 65px, the egg surface just grazes the arena centerline at clash scale,
     * creating a clean surface-tap rather than deep penetration.
     * Tuned for an 810px-wide modal — adjust if container width changes.
     */
    eggFightTranslateX: 65,

    /**
     * X translation (from each egg's natural panel-center position) that places
     * the egg exactly at the arena's horizontal center.
     * Formula: arenaWidth / 4  →  810 / 4 = 202.5
     * Both eggs use this value (left egg +202.5, right egg −202.5) so they stack
     * on the exact same vertical axis during the fight phase.
     * Adjust if the modal width changes.
     */
    eggFightCenterX: 202.5,

    /**
     * How far each egg travels along the Y axis during the fight phase so that
     * the egg tips meet exactly at the contact point with zero overlap.
     *
     * Direction: attacker moves UP (−Y), defender moves DOWN (+Y).
     * At each clash keyframe both tips converge to y = 0.
     *
     * Derivation:
     *   tip_y = travelY − (eggIdleSize / 2) × clashScale = 0
     *   → travelY = (eggIdleSize / 2) × clashScale
     *             = 125 × 1.22 = 152.5  (bounding-box edge)
     *
     * The egg image's visible tip sits slightly inside the bounding box.
     * Subtract the estimated inset (≈ 8 px) to get the visual-tip contact value:
     *   travelY ≈ 152.5 − 8 = 144.5  → 145
     *
     * Tune this value if the tips gap or overlap: increase to close a gap,
     * decrease to remove overlap. One unit = one CSS pixel.
     */
    eggFightTravelY: 145,

    /**
     * Timing and recoil amounts for the 3-clash fight sequence.
     * All values are milliseconds from the moment the fight phase starts.
     */
    fightClashes: {
      clash1Ms: 500, // ms → first clash
      clash2Ms: 1300, // ms → second clash (harder)
      clash3Ms: 2300, // ms → final devastating clash
      recoil1X: 37, // px from idle — resting position after recoil 1
      recoil2X: 27, // px from idle — resting position after recoil 2
    },

    scales: {
      idle: 1,
      fight: 1.1,
      impact: 1.22, // scale at clash 1
      clash2: 1.26, // scale at clash 2
      clash3: 1.34, // scale at final clash
      broken: 0.58,
    },

    /** Rotation (degrees) the broken egg spins when it loses */
    brokenEggRotation: 46,
    /** Extra pixels the broken egg flies back from its collision position */
    brokenEggFlybackX: 55,
    /** Opacity the broken egg fades to after losing */
    brokenEggOpacity: 0.42,

    durations: {
      eggMove: 0.68, // seconds — kept for back-compat
      fightSequence: 2.8, // seconds — total fight keyframe animation duration
      eggReturn: 0.55, // seconds — winner egg settle
      eggBreak: 0.38, // seconds — loser break flyback
      impactStar: 0.36, // seconds — star burst appearance
      resultSlideIn: 0.42, // seconds — round result overlay entrance
      gameOverFadeIn: 0.6, // seconds — game over screen fade in
      countdownPop: 0.28, // seconds — each countdown number pop
    },

    easing: {
      eggMove: [0.25, 0.46, 0.45, 0.94] as [number, number, number, number],
      eggBreak: [0.55, 0, 1, 0.45] as [number, number, number, number],
      bounce: [0.34, 1.56, 0.64, 1] as [number, number, number, number],
      snappy: [0.22, 1, 0.36, 1] as [number, number, number, number],
    },

    shake: {
      intensityX: 20, // pixels horizontal (impact phase)
      intensityY: 12, // pixels vertical
      duration: 0.52, // seconds

      // Per-clash shake intensities — escalating
      clash1X: 10,
      clash1Y: 6,
      clash1Duration: 0.3,

      clash2X: 17,
      clash2Y: 10,
      clash2Duration: 0.38,

      clash3X: 28,
      clash3Y: 17,
      clash3Duration: 0.55,
    },

    impactStar: {
      maxScale: 1.32,
      rotateOffset: 20, // degrees rotation at peak
    },

    /** How many times the role watermark text is repeated vertically */
    watermarkRepeatCount: 6,
  },

  /** Egg image display size in pixels when idle/in countdown */
  eggIdleSize: 270,
  eggInFightSize: 180,

  /** Opacity of the large watermark text behind each egg */
  watermarkOpacity: 0.075,

  colors: {
    you: "#2563eb",
    enemy: "#ef4444",
    roundWin: "#22c55e",
    gold: "#ffd700",
    impactStar: "#ffe000",
  },
} as const;
